import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PathwayItem } from '../types';

interface ConsolidatedItem {
  id: string;
  type: 'module' | 'set' | 'consolidated-sets';
  name: string;
  description: string;
  module_id: string;
  set_ids?: string[];
  order: number;
  worksheet_from: number;
  worksheet_to: number;
  itemCount?: number; // Number of sets consolidated
}

// Helper function to consolidate consecutive sets
const consolidateItems = (items: PathwayItem[]): ConsolidatedItem[] => {
  const consolidated: ConsolidatedItem[] = [];
  let i = 0;

  while (i < items.length) {
    const currentItem = items[i];

    // If it's a module, add it as-is
    if (currentItem.type === 'module') {
      consolidated.push({
        ...currentItem,
        type: 'module'
      });
      i++;
      continue;
    }

    // If it's a set, check if we can consolidate with following sets
    if (currentItem.type === 'set') {
      const setGroup: PathwayItem[] = [currentItem];
      let j = i + 1;

      // Look ahead for consecutive sets from the same module
      while (j < items.length && items[j].type === 'set' && items[j].module_id === currentItem.module_id) {
        const prevItem = items[j - 1];
        const nextItem = items[j];
        
        // Check if worksheets are consecutive
        if (nextItem.worksheet_from === prevItem.worksheet_to + 1) {
          setGroup.push(nextItem);
          j++;
        } else {
          break;
        }
      }

      // If we have multiple consecutive sets, consolidate them
      if (setGroup.length > 1) {
        const firstSet = setGroup[0];
        const lastSet = setGroup[setGroup.length - 1];
        
        consolidated.push({
          id: firstSet.id, // Use first set's ID for sorting
          type: 'consolidated-sets',
          name: firstSet.name.replace(/-\d+-\d+$/, `-${firstSet.worksheet_from}-${lastSet.worksheet_to}`),
          description: `${setGroup.length} sets consolidated`,
          module_id: firstSet.module_id,
          set_ids: setGroup.map(s => s.id),
          order: firstSet.order,
          worksheet_from: firstSet.worksheet_from,
          worksheet_to: lastSet.worksheet_to,
          itemCount: setGroup.length
        });
        i = j;
      } else {
        // Single set, add as-is
        consolidated.push({
          ...currentItem,
          type: 'set'
        });
        i++;
      }
    }
  }

  return consolidated;
};

interface BuilderItemProps {
  item: ConsolidatedItem;
  onRemove: (id: string | string[]) => void;
}

const BuilderItem: React.FC<BuilderItemProps> = ({ item, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getBadgeText = () => {
    if (item.type === 'module') return 'Module';
    if (item.type === 'consolidated-sets') return `Set`;
    return 'Set';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`builder-item ${isDragging ? 'dragging' : ''}`}
      onClick={(e) => {
        // Prevent drag from interfering with button clicks
        if ((e.target as HTMLElement).tagName === 'BUTTON') {
          e.stopPropagation();
        }
      }}
    >
      <span className="drag-handle">⋮⋮</span>
      <span className={`item-badge ${item.type === 'module' ? 'module' : 'set'}`}>
        {getBadgeText()}
      </span>
      <div className="builder-item-info">
        <div className="builder-item-name">{item.name}</div>
        <div className="builder-item-description">{item.description}</div>
      </div>
      <div style={{ 
        background: '#f3f4f6', 
        padding: '4px 8px', 
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        color: '#666'
      }}>
        #{item.order}
      </div>
      <button
        className="remove-button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.type === 'consolidated-sets' && item.set_ids ? item.set_ids : item.id);
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        Remove
      </button>
    </div>
  );
};

interface BuilderPanelProps {
  items: PathwayItem[];
  onRemove: (id: string | string[]) => void;
}

const BuilderPanel: React.FC<BuilderPanelProps> = ({ items, onRemove }) => {
  const { setNodeRef } = useDroppable({
    id: 'builder-droppable',
  });

  const consolidatedItems = consolidateItems(items);

  return (
    <div ref={setNodeRef} className="builder-items-container">
      {consolidatedItems.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎯</div>
          <h3>Build Your Pathway</h3>
          <p>Drag modules or sets from the left panel to build your learning pathway</p>
        </div>
      ) : (
        <SortableContext
          items={consolidatedItems.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {consolidatedItems.map((item) => (
            <BuilderItem key={item.id} item={item} onRemove={onRemove} />
          ))}
        </SortableContext>
      )}
    </div>
  );
};

export default BuilderPanel;